import axios from "axios";
import { useEffect, useState } from "react";

export type FeaturedTripCreator = {
	name?: string;
	username?: string;
	pictureUrl?: string;
};

export type FeaturedTrip = {
	id: number;
	name: string;
	tripId: string;
	description?: string;
	posterUrl?: string;
	videoUrl: string;
	creator: FeaturedTripCreator;
};

const ENDPOINT =
	"https://cms.stippl.io/api/featured-trips" +
	"?populate[creator][populate][0]=picture" +
	"&populate=videoPoster" +
	"&filters[section][$eq]=section-1" +
	"&sort=order:asc";

let cache: FeaturedTrip[] | null = null;
let inflight: Promise<FeaturedTrip[]> | null = null;

const pickPosterUrl = (videoPoster: any): string | undefined => {
	const formats = videoPoster?.data?.attributes?.formats;
	return formats?.medium?.url || formats?.large?.url || formats?.small?.url || formats?.thumbnail?.url || videoPoster?.data?.attributes?.url;
};

const pickCreatorPictureUrl = (creator: any): string | undefined => {
	const formats = creator?.data?.attributes?.picture?.data?.attributes?.formats;
	return formats?.thumbnail?.url || formats?.small?.url || creator?.data?.attributes?.picture?.data?.attributes?.url;
};

const mapItem = (item: any): FeaturedTrip => {
	const a = item?.attributes || {};
	const creatorAttrs = a?.creator?.data?.attributes;
	return {
		id: item.id,
		name: a.name,
		tripId: a.tripId,
		description: a.description || undefined,
		posterUrl: pickPosterUrl(a.videoPoster),
		videoUrl: `https://video.stippl.io/trip-${a.tripId}.mp4`,
		creator: {
			name: creatorAttrs?.name,
			username: creatorAttrs?.username,
			pictureUrl: pickCreatorPictureUrl(a.creator),
		},
	};
};

const fetchFeaturedTrips = async (): Promise<FeaturedTrip[]> => {
	const res = await axios.get(ENDPOINT);
	const items: any[] = res?.data?.data || [];
	return items.filter((i) => i?.attributes?.tripId).map(mapItem);
};

export const useFeaturedTrips = (): { trips: FeaturedTrip[]; loading: boolean; error: boolean } => {
	const [trips, setTrips] = useState<FeaturedTrip[]>(cache ?? []);
	const [loading, setLoading] = useState<boolean>(cache === null);
	const [error, setError] = useState<boolean>(false);

	useEffect(() => {
		if (cache) {
			return undefined;
		}
		if (!inflight) {
			inflight = fetchFeaturedTrips()
				.then((data) => {
					cache = data;
					return data;
				})
				.catch((e) => {
					console.warn("[FeaturedTrips] CMS fetch failed:", e?.message || e);
					cache = [];
					return [] as FeaturedTrip[];
				})
				.finally(() => {
					inflight = null;
				});
		}
		let cancelled = false;
		inflight.then((data) => {
			if (cancelled) return;
			setTrips(data);
			setLoading(false);
			setError(data.length === 0);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	return { trips, loading, error };
};
